import React from 'react';
import { useHistory } from 'react-router-dom';
import { Btn, Bx, Ctn, DropDownMenu, IconBtn, IIconBtnProps, Link, TabItem, TabList, Typo } from '@curry-group/mui-curcuma';
import { faArrowLeft, faBookmark as faBookmarkLight, faCameraHome, faCog, faCommentSlash, faEllipsisV, faInfo, faLongArrowLeft, faMapPin, faPlus, faRepeat, faShare, faTimes, faTools, faUserPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fade, Grid, Icon, MenuItem } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { getProfileRouteByUserId, removeDetailUrl, shorten, TYPES } from '../../../helper';
import { useIsMobile } from '../../../helper/hooks/isMobile';
import { HeadBar } from '../../head-bar';
import { MobileHeadBar } from '../../mobile-head-bar';
import { faBookmark } from '@fortawesome/pro-solid-svg-icons';
import { DeactivatedIcon } from '../../deactivated-icon';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMessagesRequest, pinMessageRequest, setActiveTab, setActiveView, setInitialMessage, setScrollMessage } from '../../../data/reducer/communication';
import { IMessageModel } from '../../../model/communication/Message';
import { RoleType } from '../../../model/communication/Role';
import _ from 'lodash';

export interface IHeaderProps {
  alias?: string;
  parentId?: string; // communication id
  working?: boolean;
  title?: string;
  subtitle?: string;
  isDeactivated?: boolean;
  partnerUserId?: string;
  actions?: Array<ICommunicationAction>;
  metaActions?: Array<ICommunicationAction>;
  metaInfo?: React.ReactNode;

  tabs?: string[];
  selectedTab?: number;
  selectTab?: (tab: number) => void;
  selectedTabValue?: string;
  backButtonAction?: () => void;
  /**
   * Hides the desktop back arrow. Used by the bot chat, where the sidebar is
   * always on screen and "back" has nothing to go back to; on mobile the arrow
   * stays, because there it is the way back to the session list.
   */
  hideBackButton?: boolean;
  isSettings?: boolean;
  adminMissing?: boolean;
  pinnedMessage?: IMessageModel;
  pinnedMessageText?: string;
  loadedMessageIds?: Array<string>; // loaded message ids
  isCommAdmin?: boolean; // is communication admin
  activeView?: string; // active view in communication
}

export const Header: React.FC<IHeaderProps> =
({
  alias,
  parentId,
  working,
  title,
  subtitle,
  isDeactivated,
  actions,
  metaActions,
  metaInfo,
  tabs,
  selectedTab,
  selectTab,
  selectedTabValue,
  backButtonAction,
  hideBackButton,
  isSettings,
  adminMissing,
  partnerUserId,
  pinnedMessage,
  pinnedMessageText,
  loadedMessageIds,
  isCommAdmin,
  activeView
}) => {
  const linkToProfile = (userId?: string, lnkText?: string) => {
    if (!userId) return lnkText;
    const link = getProfileRouteByUserId(userId);
    return (
      <Link underline="hover" to={link}>
        {!!lnkText ? lnkText : link}
      </Link>
    );
  };

  const pinnedMessageContent = !!pinnedMessageText ? pinnedMessageText : pinnedMessage?.content;

  const history = useHistory();
  const dispatch = useDispatch();

  const contentScrolled = true;
  
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    selectTab?.(newValue);
  };
  return (<>
    <HeadBar backButtonLink={backButtonAction} flexShrink={0} pb={0} zIndex={1} transparent={!contentScrolled} id="content-head-container">
      <Fade in={contentScrolled}>
        <Bx flexShrink={1} flexGrow={1}>
          <Bx height="100%" display="flex" alignItems="center">
            <Ctn>
              <Grid container>
                <Grid md={7} item>
                  <Bx display="flex" alignItems="center" height={!!subtitle ? 'auto' : '100%'}>
                    {!isSettings && !hideBackButton && <Bx display="flex" pr={1} alignItems="center" justifyContent="flex-start" height="100%">
                      <IconBtn size="small"
                        onClick={() => {history.goBack();}}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                      </IconBtn>
                    </Bx>}
                    <Typo component="span" variant="h2">
                      {!working && !!title && isDeactivated && (
                        <span style={{ marginRight: 8 }} title="Inaktiv">
                          <DeactivatedIcon title="Inaktiv" />
                        </span>
                      )}
                      {working
                        ? <Skeleton animation="wave" style={{ width: '200px' }} />
                        : !!title
                          ? linkToProfile(partnerUserId, title)
                          : <Skeleton animation="wave" style={{ width: '200px' }} />
                      }
                    </Typo>
                  </Bx>
                  {/* The inset lines the subtitle up with the title past the back arrow; without the arrow it would sit indented. */}
                  {!!subtitle && <Bx display="flex" alignItems="center" justifyContent="flex-start" pl={hideBackButton ? 0 : 5.5}>
                    <Typo component="span">
                      {working ? <Skeleton animation="wave" style={{ width: '200px' }} /> : subtitle ? subtitle : <Skeleton animation="wave" style={{ width: '200px' }} />}
                    </Typo>
                  </Bx>}
                </Grid>
                <Grid md={5} item>
                  <Bx display="flex" justifyContent="flex-end" alignItems="center">
                    {actions &&
                      actions.map((action, idx) => {
                        const { specificType, ...validBtnAction } = action;
                        if (specificType === 'IconBtn')
                          return (
                            <Bx key={idx} mr={2}>
                              <IconBtn disabled={working} {...validBtnAction}>
                                {action.icon}
                              </IconBtn>
                            </Bx>
                          );
                        else if (specificType === 'Btn')
                          return (
                            <Bx key={idx} mr={1} display="flex" justifyContent="center" alignItems="center">
                              <Btn {...validBtnAction} startIcon={action.icon}>
                                {action.title}
                              </Btn>
                            </Bx>
                          );
                        else return <></>;
                      })}
                  </Bx>
                </Grid>
                {!!tabs?.length && (
                  <Grid md={5} item>
                    <Bx mt={1.5}>
                      <TabList
                        value={selectedTab}
                        fontVariant="body1"
                        textColor="primary"
                        onChange={handleChange as ((event: React.ChangeEvent<{}>, value: any) => void) & ((event: React.FormEvent<HTMLButtonElement>) => void)}
                      >
                        {tabs.map((t, i) => (
                          <TabItem key={i} disabled={working} label={t} />
                        ))}
                      </TabList>
                    </Bx>
                  </Grid>
                )}
                <Grid md={!!tabs?.length ? 7 : 12} item>
                  <Bx mt={1.5} pb={1.5} display="flex" justifyContent="flex-end" alignItems="center">
                    {!!metaInfo && (
                      <Bx mr="auto" pl={hideBackButton ? 0 : 5.5} display="flex" alignItems="center">
                        {metaInfo}
                      </Bx>
                    )}
                    {metaActions &&
                      metaActions.map((action, idx) => {
                        const { specificType, ...validBtnAction } = action;
                        if (specificType === 'IconBtn')
                          return (
                            <Bx key={idx} mr={2}>
                              <IconBtn disabled={working} {...validBtnAction}>
                                {action.icon}
                              </IconBtn>
                            </Bx>
                          );
                        else if (specificType === 'Btn')
                          return (
                            <Bx key={idx} mr={1} display="flex" justifyContent="center" alignItems="center">
                              <Btn {...validBtnAction} startIcon={action.icon}>
                                {action.title}
                              </Btn>
                            </Bx>
                          );
                        else return <></>;
                      })}
                  </Bx>
                </Grid>
              </Grid>
            </Ctn>
          </Bx>
        </Bx>
      </Fade>
    </HeadBar>
    {adminMissing && (
      <Bx display="flex"
        justifyContent="flex-start"
        px={{ xs: 2, lg: 5 }}
        py={0}
        bgcolor={'accent.light'}
        style={{ borderBottom: '1px solid #D6D7D9' }}
      >
        <Bx
          display="flex"
          justifyContent="flex-start"
          py={1}
          onClick={() => {
            dispatch(setActiveView({ view: 'settings' }));
            dispatch(setActiveTab({tab: {
              title: 'Teilnehmende',
              alias: 'participants',
              type: 'view'
            }}));
          }}
          style={{ cursor: 'pointer' }}
        >
          <Typo variant="h2" color="textSecondary">
            <FontAwesomeIcon icon={faTools} />
          </Typo>
          &nbsp;
          &nbsp;
          <Typo variant="body1" color="textSecondary" style={{ lineHeight: 2 }}>
            Bitte benennen Sie von den Teilnehmern einen weiteren Administrator.
          </Typo>
        </Bx>
      </Bx>
    )}
    {activeView === 'chat' && selectedTabValue === 'main' && !!pinnedMessage && (
      <Bx display="flex"
        justifyContent="flex-start"
        px={{ xs: 2, lg: 5 }}
        py={0}
        bgcolor={'accent.light'}
        style={{ borderBottom: '1px solid #D6D7D9' }}
      >
        <Bx
          display="flex"
          justifyContent="flex-start"
          py={1}
          onClick={() => {
            if (!pinnedMessage.id) return;
            if (loadedMessageIds?.includes(pinnedMessage.id)) {
              dispatch(setScrollMessage({ scrollToMessage: pinnedMessage.id }));
            } else {
              const threadMessage = pinnedMessage.threadRoot || '';
              if (!!threadMessage) {
                dispatch(
                  setInitialMessage({
                    initialMessage: threadMessage,
                    messageInThread: pinnedMessage.id,
                    openDetail: false
                  }));
                dispatch(
                  fetchMessagesRequest({
                    reset: true,
                    alias: !!threadMessage ? threadMessage : 'main',
                    communicationId: parentId ?? '',
                    message: pinnedMessage.id,
                    threadRoot: threadMessage,
                  })
                );
              } else {
                dispatch(
                  setInitialMessage({
                    initialMessage: pinnedMessage.id,
                    openDetail: false,
                  }));
                dispatch(
                  fetchMessagesRequest({
                    reset: false,
                    alias: 'main',
                    communicationId: parentId ?? '',
                    message: pinnedMessage.id,
                  })
                );
              }
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <Typo variant="h2" style={{ color: '#ff0000' }}>
            <FontAwesomeIcon icon={faMapPin} />
          </Typo>
          &nbsp;
          &nbsp;
          <Typo variant="body1" color="textSecondary" style={{ lineHeight: 2 }}>
            {!!pinnedMessageContent ? shorten(pinnedMessageContent, 150) : 'Gepinnte Nachricht'}
          </Typo>
        </Bx>
        {isCommAdmin && pinnedMessage && pinnedMessage.id && (
          <Bx
            display="flex"
            justifyContent="flex-start"
            ml={'auto'}
          >
            <Typo variant="h2" color="textSecondary" style={{ lineHeight: 1.5 }}>
              <IconBtn size="small"
                onClick={() => 
                  dispatch(
                    pinMessageRequest({
                      id: parentId ?? '',
                      messageId: pinnedMessage.id,
                    })
                  )
                }
              >
                <FontAwesomeIcon icon={faTimes} />
              </IconBtn>
            </Typo>
          </Bx>
        )}
      </Bx>
    )}
  </>);
};

export const MobileHeader: React.FC<IHeaderProps> = ({
  alias,
  parentId,
  working,
  title,
  subtitle,
  isDeactivated,
  actions,
  metaActions,
  metaInfo,
  tabs,
  selectedTab,
  selectTab,
  selectedTabValue,
  backButtonAction,
  adminMissing,
  pinnedMessage,
  pinnedMessageText,
  loadedMessageIds,
  isCommAdmin,
  activeView
}) => {
  const dispatch = useDispatch();

  const pinnedMessageContent = !!pinnedMessageText ? pinnedMessageText : pinnedMessage?.content;

  const [open, setOpen] = React.useState<null | string>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement | HTMLButtonElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget.getAttribute('aria-controls'));
    setAnchorEl(event.currentTarget);
  };
  const handleMenu = (menuAction?: (e) => void) => {
    setOpen(null);
    setAnchorEl(null);
    menuAction && menuAction(null);
  };
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    selectTab?.(newValue);
  };
  return (
    <React.Fragment>
      <MobileHeadBar id="content-head-container">
        <Bx display="flex" height="100%" alignItems="center" flexDirection="column" pt={1}>
          <Bx display="flex" alignItems="center" width="100%">
            <Bx zIndex={2}>
              <Bx pr={1}>
                {backButtonAction && (
                  <IconBtn size="medium" onClick={backButtonAction}>
                    <FontAwesomeIcon icon={faLongArrowLeft} />
                  </IconBtn>
                )}
                {!backButtonAction && (
                  <IconBtn size="medium" to={window.location.origin + removeDetailUrl(window.location.pathname)}>
                    <FontAwesomeIcon icon={faLongArrowLeft} />
                  </IconBtn>
                )}
              </Bx>
            </Bx>
            <Bx display="flex" alignItems="center" height="100%" flexGrow={1} flexShrink={1} overflow="hidden">
              <Typo component="span" variant="h3" noWrap>
                {!working && !!title && isDeactivated && (
                  <span style={{ marginRight: 6 }} title="Inaktiv">
                    <DeactivatedIcon title="Inaktiv" />
                  </span>
                )}
                {working ? <Skeleton animation="wave" style={{ width: '200px' }} /> : title ? title : <Skeleton animation="wave" style={{ width: '200px' }} />}
              </Typo>
            </Bx>
            {actions && !!actions.length && (
              <Bx ml="auto" pl={2} flexGrow={0} flexShrink={0} flexBasis="auto">
                <Bx>
                  <IconBtn size="small" onClick={handleClick} color="default" variant="text" aria-controls="more" aria-haspopup="true">
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </IconBtn>
                </Bx>
              </Bx>
            )}
          </Bx>
          {!!subtitle && <Bx pl={7} width={'100%'} display="flex" alignItems="center" justifyContent="flex-start">
            <Typo component="span">
              {working ? <Skeleton animation="wave" style={{ width: '200px' }} /> : subtitle ? subtitle : <Skeleton animation="wave" style={{ width: '200px' }} />}
            </Typo>
          </Bx>}
          {!!metaInfo && (
            <Bx pl={7} mt={1} width={'100%'} display="flex" alignItems="center" justifyContent="flex-start">
              {metaInfo}
            </Bx>
          )}
          {!!tabs?.length && (
            <Bx display="flex" alignItems="center" width="100%" mt={1}>
              <TabList
                value={selectedTab}
                fontVariant="body1"
                textColor="primary"
                variant="scrollable"
                onChange={handleChange as ((event: React.ChangeEvent<{}>, value: any) => void) & ((event: React.FormEvent<HTMLButtonElement>) => void)}
              >
                {tabs.map((tab, idx) => (
                  <TabItem key={idx} disabled={working} label={tab} ml={idx ? undefined : 1} />
                ))}
              </TabList>
            </Bx>
          )}
        </Bx>
      </MobileHeadBar>
      {adminMissing && (
        <Bx display="flex"
          justifyContent="flex-start"
          px={{ xs: 2, lg: 5 }}
          py={0}
          bgcolor={'accent.light'}
          style={{ borderBottom: '1px solid #D6D7D9' }}
        >
          <Bx
            display="flex"
            justifyContent="flex-start"
            py={1}
            onClick={() => {
              dispatch(setActiveView({ view: 'settings' }));
              dispatch(setActiveTab({tab: {
                title: 'Teilnehmende',
                alias: 'participants',
                type: 'view'
              }}));
            }}
            style={{ cursor: 'pointer' }}
          >
            <Typo variant="h2" color="textSecondary">
              <FontAwesomeIcon icon={faTools} />
            </Typo>
            &nbsp;
            &nbsp;
            <Typo variant="body1" color="textSecondary" style={{ lineHeight: 2 }}>
              Bitte benennen Sie von den Teilnehmern einen weiteren Administrator.
            </Typo>
          </Bx>
        </Bx>
      )}
      {activeView === 'chat' && selectedTabValue === 'main' && !!pinnedMessage && (
        <Bx display="flex"
          justifyContent="flex-start"
          px={{ xs: 2, lg: 5 }}
          py={0}
          bgcolor={'accent.light'}
          style={{ borderBottom: '1px solid #D6D7D9' }}
        >
          <Bx
            display="flex"
            justifyContent="flex-start"
            py={1}
            onClick={() => {
              if (!pinnedMessage.id) return;
              if (loadedMessageIds?.includes(pinnedMessage.id)) {
                dispatch(setScrollMessage({ scrollToMessage: pinnedMessage.id }));
              } else {
                const threadMessage = pinnedMessage.threadRoot || '';
                if (!!threadMessage) {
                  dispatch(
                    setInitialMessage({
                      initialMessage: threadMessage,
                      messageInThread: pinnedMessage.id,
                      openDetail: false
                    }));
                  dispatch(
                    fetchMessagesRequest({
                      reset: true,
                      alias: !!threadMessage ? threadMessage : 'main',
                      communicationId: parentId ?? '',
                      message: pinnedMessage.id,
                      threadRoot: threadMessage,
                    })
                  );
                } else {
                  dispatch(
                    setInitialMessage({
                      initialMessage: pinnedMessage.id,
                      openDetail: false,
                    }));
                  dispatch(
                    fetchMessagesRequest({
                      reset: false,
                      alias: 'main',
                      communicationId: parentId ?? '',
                      message: pinnedMessage.id,
                    })
                  );
                }
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <Typo variant="h2" color="error">
              <FontAwesomeIcon icon={faMapPin} />
            </Typo>
            &nbsp;
            &nbsp;
            <Typo variant="body1" color="textSecondary" style={{ lineHeight: 2 }}>
              {!!pinnedMessageContent ? shorten(pinnedMessageContent, 150) : 'Gepinnte Nachricht'}
            </Typo>
          </Bx>
          {isCommAdmin && pinnedMessage && pinnedMessage.id && (
            <Bx
              display="flex"
              justifyContent="flex-start"
              ml={'auto'}
            >
              <Typo variant="h2" color="textSecondary" style={{ lineHeight: 1.5 }}>
                <IconBtn size="small"
                  onClick={() => 
                    dispatch(
                      pinMessageRequest({
                        id: parentId ?? '',
                        messageId: pinnedMessage.id,
                      })
                    )
                  }
                >
                  <FontAwesomeIcon icon={faTimes} />
                </IconBtn>
              </Typo>
            </Bx>
          )}
        </Bx>
      )}
      <DropDownMenu
        id={open ? open : undefined}
        elevation={3}
        getContentAnchorEl={null}
        anchorReference="anchorEl"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        onClose={() => setOpen(null)}
        MenuListProps={{ component: 'div' }}
        open={open !== null}
        borderRadius="borderRadius"
      >
        <Bx bgcolor="primary.main" color="primary.contrastText">
          {actions &&
            actions.map((action, idx) => (
              <MenuItem key={idx} onClick={() => handleMenu(action.onClick)}>
                {action.title}
              </MenuItem>
            ))}
        </Bx>
      </DropDownMenu>
    </React.Fragment>
  );
};

export interface GetMessagesRequest {
  before?: number; // before unix
  since?: number; // since unix

  threadRoot?: string; // thread root

  numThreadChildren?: number; // num thread children

  message?: string; // page with message
}

export interface Tab {
  type: 'messages' | 'files' | 'view';
  title: string;
  alias: string;
}
export interface CommunicationTab extends Tab {
  request?: GetMessagesRequest;
}

export interface ICommunicationAction extends IIconBtnProps {
  icon: JSX.Element;
  specificType: 'IconBtn' | 'Btn';
}

export interface ICommunicationHeaderProps {
  alias?: string;
  working?: boolean;
  title?: string;
  subtitle?: string;

  tabs: Tab[];
  selectedTab: Tab;
  setSelectedTab: (tab: Tab) => void;

  openInfo?: () => void;
  openSettings?: () => void;
  openAddParticipants?: () => void;
  allowConferencing?: boolean;
  onConferenceClick?: () => void;
  /** Additional actions, placed directly left of the video conference button. */
  extraActions?: Array<ICommunicationAction>;

  oneToOneStatus?: {
    onResendInvitationClick?: () => void;
    onRevokeInvitationClick?: () => void;
  }

  shareClick?: () => void;
  memoryClick?: () => void;
  booked?: boolean;
  partnerUserId?: string;
  isDeactivated?: boolean;

  isAdmin?: boolean;
}

export const CommunicationHeader: React.FC<ICommunicationHeaderProps> = ({
  alias,
  allowConferencing,
  onConferenceClick,
  extraActions,
  working,
  title,
  subtitle,
  tabs,
  selectedTab,
  setSelectedTab,
  openInfo,
  openSettings,
  openAddParticipants,
  oneToOneStatus,
  shareClick,
  memoryClick,
  booked,
  partnerUserId,
  isDeactivated,
  isAdmin = false
}) => {
  const communicationActions: Array<ICommunicationAction> = [];
  if (oneToOneStatus) {
    communicationActions.push({
      specificType: 'IconBtn',
      onClick: oneToOneStatus.onResendInvitationClick,
      icon: <FontAwesomeIcon icon={faRepeat} />,
      title: 'Erneut einladen',
      size: 'small'
    });
  }
  if (oneToOneStatus) {
    communicationActions.push({
      specificType: 'IconBtn',
      onClick: oneToOneStatus.onRevokeInvitationClick,
      icon: <FontAwesomeIcon icon={faCommentSlash} />,
      title: 'Einladung zurückziehen',
      variant: 'contained',
      color: 'error'
    });
  }
  if (openAddParticipants) {
    communicationActions.push({
      specificType: 'IconBtn',
      onClick: () => openAddParticipants(),
      icon: <FontAwesomeIcon icon={faUserPlus} />,
      title: 'Teilnehmende einladen',
      size: 'small'
    });
  }
  if (openSettings) {
    communicationActions.push({
      specificType: 'IconBtn',
      onClick: () => openSettings(),
      icon: <FontAwesomeIcon icon={faCog} />,
      title: 'Einstellungen',
      size: 'small'
    });
  }
  if (openInfo) {
    communicationActions.push({
      specificType: 'IconBtn',
      onClick: () => openInfo(),
      icon: <FontAwesomeIcon icon={faInfo} />,
      title: 'Informationen',
      size: 'small'
    });
  }
  if (extraActions?.length) {
    communicationActions.push(...extraActions);
  }
  if (allowConferencing && onConferenceClick) {
    if (!openInfo) {
      communicationActions.push({
        specificType: 'IconBtn',
        onClick: () => onConferenceClick(),
        icon: <FontAwesomeIcon icon={faCameraHome} />,
        title: 'Videokonferenz starten',
        variant: 'contained',
        color: 'secondary'
      });
    } else {
      if (selectedTab.alias === 'sharepoints' && isAdmin) {
        communicationActions.push({
          specificType: 'Btn',
          onClick: () => onConferenceClick(),
          icon: <FontAwesomeIcon icon={faPlus} />,
          title: 'Sharepoint erstellen',
          variant: 'contained',
          color: 'secondary'
        });
      } else {
        communicationActions.push({
          specificType: 'Btn',
          onClick: () => onConferenceClick(),
          icon: <FontAwesomeIcon icon={faPlus} />,
          title: 'Meeting erstellen',
          variant: 'contained',
          color: 'secondary'
        });
      }
    }
  }
  const metaActions: Array<ICommunicationAction> = [];
  if (shareClick) {
    metaActions.push({
      specificType: 'Btn',
      onClick: () => shareClick(),
      icon: <FontAwesomeIcon icon={faShare} />,
      title: 'Teilen',
      variant: 'contained',
      color: 'primary'
    });
  }
  if (memoryClick) {
    metaActions.push({
      specificType: 'Btn',
      onClick: () => memoryClick(),
      icon: <FontAwesomeIcon icon={booked ? faBookmark : faBookmarkLight} />,
      title: booked ? 'Gemerkt' : 'Merken',
      variant: booked ? 'contained' : 'outlined',
      color: 'primary'
    });
  }

  const HeaderProps: IHeaderProps = {
    working: working,
    title: title,
    partnerUserId: partnerUserId,
    subtitle: subtitle,
    isDeactivated: isDeactivated,
    actions: communicationActions,
    metaActions: metaActions,
    selectTab: index => setSelectedTab(tabs[index]),
    selectedTab: tabs.indexOf(selectedTab),
    selectedTabValue: selectedTab.alias,
    tabs: tabs.map(t => t.title),
    pinnedMessage: undefined,
    alias: alias,
  };

  HeaderProps.backButtonAction = undefined;
  return <ConnectedHeader {...HeaderProps} />;
};

export const ConnectedHeader: React.FC<IHeaderProps> = props => {
  const isMobile = useIsMobile();
  const detailType = useSelector(state => state.detail.item?.type);
  const communicationParticipants = useSelector(state => state.communication?.participants);
  const roles = useSelector(state => state.communication?.communication?.roles);
  const userId = useSelector(state => state.foundation?.profile?.userId);
  const pinnedMessage = useSelector(state => state.detail?.item?.content?.pinnedMessage);
  const pinnedMessageText = useSelector(state => state.detail?.item?.content?.pinnedMessageText);
  const detailDeactivated = useSelector(state => state.detail?.item?.content?.deactivated);
  const id = useSelector(state => state.communication?.id);
  const messageState = useSelector(state => state.communication?.messages?.['main']);
  const activeView = useSelector(state => state.communication?.activeView);
  // Prefer the explicit prop; fall back to the live detail state so the chat
  // header lights up even when the caller didn't pass it explicitly.
  const isDeactivated = props.isDeactivated ?? !!detailDeactivated;
  
  const participant = communicationParticipants?.find(p => p.user === userId);
  const participantRoles = _.intersectionWith(roles, (participant?.roles ?? []), (role, roleId) => {
    return role?.type !== RoleType.DEFAULT && role?.roleId === roleId;
  });
  const isCommAdmin = participantRoles?.find(r => r.type === RoleType.ADMINISTRATOR) !== undefined;

  const loadedMessageIds = messageState?.messages?.map(m => m.id) ?? [];

  let adminMissing = false;
  if (communicationParticipants && (detailType === TYPES.GROUP || detailType === TYPES.PROJECT)) {
    const adminRoleId = roles?.find(role => role.type === RoleType.ADMINISTRATOR)?.roleId || '';
    const admins = communicationParticipants?.filter(participant => participant.roles.includes(adminRoleId));
    const ownParticipation = communicationParticipants?.find(participant => participant.user === userId);
    if (ownParticipation?.roles?.includes(adminRoleId)) {
      adminMissing = !admins || admins.length < 2;
    }
  }

  if (isMobile) {
    return <MobileHeader {...props}
        activeView={activeView}
        adminMissing={adminMissing}
        isCommAdmin={isCommAdmin}
        pinnedMessage={pinnedMessage}
        pinnedMessageText={pinnedMessageText}
        parentId={id}
        loadedMessageIds={loadedMessageIds}
        isDeactivated={isDeactivated}
      />;
  }  else {
    return <Header {...props}
        activeView={activeView}
        adminMissing={adminMissing}
        isCommAdmin={isCommAdmin}
        pinnedMessage={pinnedMessage}
        pinnedMessageText={pinnedMessageText}
        parentId={id}
        loadedMessageIds={loadedMessageIds}
        isDeactivated={isDeactivated}
      />;
  }
};
